import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { Select } from '../components/Select';
import { FileUpload } from '../components/FileUpload';

export const AgentProfileEditPage = () => {
  // Basic Information
  const [fullName, setFullName] = useState('Sarah Johnson');
  const [title, setTitle] = useState('Senior Literary Agent');
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [bio, setBio] = useState('Sarah Johnson has been representing authors for over 15 years, specializing in commercial and literary fiction. She has a particular passion for character-driven narratives and diverse voices. Her clients have appeared on the New York Times bestseller list and won numerous literary awards.');

  // Agency Information
  const [agencyName, setAgencyName] = useState('Literary Partners Agency');
  const [agencyWebsite, setAgencyWebsite] = useState('https://www.literarypartnersagency.com');
  const [personalWebsite, setPersonalWebsite] = useState('https://www.sarahjohnsonliterary.com');
  const [twitter, setTwitter] = useState('@SarahJLiterary');
  const [linkedin, setLinkedin] = useState('');

  // MSWL and Genre Preferences
  const [primaryGenres, setPrimaryGenres] = useState<string[]>(['Literary Fiction', 'Commercial Fiction', 'Women\'s Fiction']);
  const [mswlDescription, setMswlDescription] = useState('I\'m actively seeking compelling character-driven fiction with strong voices and unique perspectives. Particularly interested in:\n\n- Literary fiction that tackles contemporary issues\n- Commercial fiction with literary sensibilities\n- Women\'s fiction with depth and nuance\n- Stories featuring diverse and underrepresented voices\n- Narratives that explore family dynamics and relationships\n\nI love manuscripts that make me think, feel, and see the world differently.');
  const [specificWants, setSpecificWants] = useState('- Dual timeline narratives\n- Stories set in international locations\n- Mother-daughter relationships\n- Books about books and writing\n- Literary thrillers with psychological depth');
  const [dealBreakers, setDealBreakers] = useState('Please note: I do not represent genre fiction (romance, sci-fi, fantasy, mystery/thriller), children\'s books, poetry, or screenplays. I also cannot take on projects that require heavy developmental editing.');

  // Submission Guidelines
  const [submissionGuidelines, setSubmissionGuidelines] = useState('Please query me via the Manuscript Match platform with:\n\n1. A concise query letter (under 300 words)\n2. A 1-2 page synopsis\n3. The first 10 pages of your manuscript\n\nPlease do not send attachments or full manuscripts unless requested. Simultaneous submissions are accepted, but please let me know if you receive an offer of representation elsewhere.');
  const [responseTimeframe, setResponseTimeframe] = useState('4-6 weeks');
  const [queryStatus, setQueryStatus] = useState<'open' | 'closed'>('open');

  const genres = [
    'Literary Fiction',
    'Commercial Fiction',
    'Mystery/Thriller',
    'Science Fiction',
    'Fantasy',
    'Romance',
    'Historical Fiction',
    'Young Adult',
    'Middle Grade',
    'Horror',
    'Women\'s Fiction',
    'Memoir',
    'Biography',
    'Self-Help',
    'Business',
    'True Crime',
  ];

  const responseTimeframes = [
    { value: '1-2 weeks', label: '1-2 weeks' },
    { value: '2-4 weeks', label: '2-4 weeks' },
    { value: '4-6 weeks', label: '4-6 weeks' },
    { value: '6-8 weeks', label: '6-8 weeks' },
    { value: '8-12 weeks', label: '8-12 weeks' },
    { value: 'No response means no', label: 'No response means no' },
  ];

  const handleGenreToggle = (genre: string) => {
    if (primaryGenres.includes(genre)) {
      setPrimaryGenres(primaryGenres.filter(g => g !== genre));
    } else if (primaryGenres.length < 6) {
      setPrimaryGenres([...primaryGenres, genre]);
    }
  };

  const handlePhotoChange = (file: File | null) => {
    if (file) {
      setProfilePhoto(file);
    }
  };

  const handleSave = () => {
    if (!fullName.trim()) {
      window.alert('Please enter your full name');
      return;
    }

    if (bio.length > 1000) {
      window.alert('Bio must be 1000 characters or less');
      return;
    }

    if (primaryGenres.length === 0) {
      window.alert('Please select at least one genre');
      return;
    }

    console.log('Saving agent profile:', {
      basicInfo: { fullName, title, profilePhoto, bio },
      agency: { agencyName, agencyWebsite, personalWebsite, twitter, linkedin },
      mswl: { primaryGenres, mswlDescription, specificWants, dealBreakers },
      submission: { submissionGuidelines, responseTimeframe, queryStatus },
    });

    window.alert('Profile updated successfully!');
  };

  const handleCancel = () => {
    if (window.confirm('Discard changes and return to dashboard?')) {
      window.location.href = '/agent/dashboard';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Edit Agent Profile
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Update your professional information and MSWL
          </p>
        </div>

        {/* Basic Information */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Basic Information
          </h2>

          <Card>
            <FileUpload
              label="Profile Photo"
              accept="image/*"
              onChange={handlePhotoChange}
              helperText="Upload a professional headshot (JPG or PNG, max 5MB)"
            />

            <div style={{ marginTop: '24px' }}>
              <Input
                type="text"
                label="Full Name *"
                placeholder="Sarah Johnson"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>

            <div style={{ marginTop: '24px' }}>
              <Input
                type="text"
                label="Professional Title"
                placeholder="Senior Literary Agent"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div style={{ marginTop: '24px' }}>
              <Textarea
                label="Bio *"
                placeholder="Tell writers about your background, experience, and what you're passionate about representing..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={6}
                required
                helperText={`${bio.length}/1000 characters`}
                maxLength={1000}
              />
            </div>
          </Card>
        </div>

        {/* Agency & Contact */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Agency & Contact Information
          </h2>

          <Card>
            <Input
              type="text"
              label="Agency Name *"
              placeholder="Literary Partners Agency"
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              required
            />

            <div style={{ marginTop: '24px' }}>
              <Input
                type="url"
                label="Agency Website"
                placeholder="https://www.agency.com"
                value={agencyWebsite}
                onChange={(e) => setAgencyWebsite(e.target.value)}
              />
            </div>

            <div style={{ marginTop: '24px' }}>
              <Input
                type="url"
                label="Personal Website"
                placeholder="https://www.yourname.com"
                value={personalWebsite}
                onChange={(e) => setPersonalWebsite(e.target.value)}
              />
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginTop: '24px',
            }}>
              <Input
                type="text"
                label="Twitter/X Handle"
                placeholder="@yourusername"
                value={twitter}
                onChange={(e) => setTwitter(e.target.value)}
              />

              <Input
                type="url"
                label="LinkedIn Profile"
                placeholder="https://linkedin.com/in/yourprofile"
                value={linkedin}
                onChange={(e) => setLinkedin(e.target.value)}
              />
            </div>
          </Card>
        </div>

        {/* MSWL & Genre Preferences */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Manuscript Wish List (MSWL)
          </h2>

          <Card style={{ marginBottom: '16px' }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '12px',
              display: 'block',
            }}>
              Primary Genres * (Select up to 6)
            </label>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
            }}>
              {genres.map((genre) => (
                <button
                  key={genre}
                  onClick={() => handleGenreToggle(genre)}
                  disabled={!primaryGenres.includes(genre) && primaryGenres.length >= 6}
                  style={{
                    padding: '8px 16px',
                    border: `2px solid ${primaryGenres.includes(genre) ? theme.colors.primary : theme.colors.border}`,
                    borderRadius: theme.borderRadius.pill,
                    backgroundColor: primaryGenres.includes(genre) ? theme.colors.primaryLight : theme.colors.white,
                    color: primaryGenres.includes(genre) ? theme.colors.primaryDark : theme.colors.textPrimary,
                    cursor: !primaryGenres.includes(genre) && primaryGenres.length >= 6 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    opacity: !primaryGenres.includes(genre) && primaryGenres.length >= 6 ? 0.5 : 1,
                  }}
                >
                  {genre}
                </button>
              ))}
            </div>
            <div style={{
              fontSize: '13px',
              color: theme.colors.textSecondary,
              marginTop: '8px',
            }}>
              {primaryGenres.length} of 6 selected
            </div>
          </Card>

          <Card style={{ marginBottom: '16px' }}>
            <Textarea
              label="What Are You Looking For? *"
              placeholder="Describe the types of manuscripts you're actively seeking. What themes, voices, or stories excite you?"
              value={mswlDescription}
              onChange={(e) => setMswlDescription(e.target.value)}
              rows={8}
              required
              helperText="This is the main description writers will see"
            />
          </Card>

          <Card style={{ marginBottom: '16px' }}>
            <Textarea
              label="Specific Wants"
              placeholder="List specific elements, tropes, or themes you're particularly interested in right now..."
              value={specificWants}
              onChange={(e) => setSpecificWants(e.target.value)}
              rows={6}
              helperText="Help writers understand your current interests"
            />
          </Card>

          <Card>
            <Textarea
              label="Deal Breakers"
              placeholder="What should writers NOT send you? Be clear to save everyone time..."
              value={dealBreakers}
              onChange={(e) => setDealBreakers(e.target.value)}
              rows={5}
              helperText="List genres or topics you don't represent"
            />
          </Card>
        </div>

        {/* Submission Guidelines */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Submission Guidelines
          </h2>

          <Card style={{ marginBottom: '16px' }}>
            <Textarea
              label="Query Submission Guidelines *"
              placeholder="Describe what you want included in queries (query letter, synopsis, sample pages, etc.)"
              value={submissionGuidelines}
              onChange={(e) => setSubmissionGuidelines(e.target.value)}
              rows={8}
              required
              helperText="Be specific about your requirements"
            />
          </Card>

          <Card style={{ marginBottom: '16px' }}>
            <Select
              label="Typical Response Timeframe *"
              value={responseTimeframe}
              onChange={(e) => setResponseTimeframe(e.target.value)}
              options={responseTimeframes}
              required
              helperText="Set realistic expectations for writers"
            />
          </Card>

          <Card>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '12px',
              display: 'block',
            }}>
              Query Status *
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '16px',
            }}>
              <button
                onClick={() => setQueryStatus('open')}
                style={{
                  padding: '20px',
                  border: `2px solid ${queryStatus === 'open' ? theme.colors.success : theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  backgroundColor: queryStatus === 'open' ? theme.colors.successLight : theme.colors.white,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{
                  fontSize: '18px',
                  marginBottom: '8px',
                }}>
                  ✓
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: theme.colors.textPrimary,
                  marginBottom: '4px',
                }}>
                  Open to Queries
                </div>
                <div style={{
                  fontSize: '13px',
                  color: theme.colors.textSecondary,
                }}>
                  Accepting new submissions
                </div>
              </button>

              <button
                onClick={() => setQueryStatus('closed')}
                style={{
                  padding: '20px',
                  border: `2px solid ${queryStatus === 'closed' ? theme.colors.error : theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  backgroundColor: queryStatus === 'closed' ? theme.colors.errorLight : theme.colors.white,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{
                  fontSize: '18px',
                  marginBottom: '8px',
                }}>
                  ✕
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: theme.colors.textPrimary,
                  marginBottom: '4px',
                }}>
                  Currently Closed
                </div>
                <div style={{
                  fontSize: '13px',
                  color: theme.colors.textSecondary,
                }}>
                  Not accepting queries now
                </div>
              </button>
            </div>
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: theme.colors.backgroundAlt,
              borderRadius: theme.borderRadius.small,
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              {queryStatus === 'open'
                ? '✓ Your profile will appear in agent searches and writers can query you'
                : '✕ Your profile will be hidden from searches and queries will be disabled'
              }
            </div>
          </Card>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
          paddingTop: '24px',
          borderTop: `1px solid ${theme.colors.border}`,
        }}>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};
